import React, {createContext, useCallback, useContext, useEffect, useRef} from "react";

// WHO HAS UNSAVED WORK, ASKED AT THE MOMENT IT MATTERS.
//
// The lifecycle buttons live at the TOP of a record -- Submit for Approval, Release, Revise -- and
// the editing happens in a TAB below them. Nothing connected the two, so pressing Release while a
// half-filled panel sat underneath released the record as it was on the server and silently
// discarded the edits. Ed did exactly that on Alpha Medical's Training Curricula 1: configured the
// curriculum, released without pressing Save, and the first symptom was a trainee reporting a
// missing assignment -- four empty tables and a RELEASED record that looked perfect.
//
// A REGISTRY, NOT A PROP CHAIN. The panels that can be dirty are reached through several layers of
// tab rendering, and threading a callback down each one means the next panel somebody adds is
// silently exempt. A panel opts in with one hook; the buttons ask the registry.
//
// HELD IN A REF, NOT IN STATE, deliberately: a panel re-reports on every render, and storing that
// in state would re-render the provider on every keystroke in every registered panel. Nothing
// renders from this -- it is read once, when a button is pressed.
//
// THE SERVER GATE IS STILL THE REAL ONE. This is a courtesy: it cannot see a panel that never
// registered, and it is trivially bypassed by a reload. Releasing something unusable is refused by
// the service (CC-TRNG-NCUR and its neighbours), which is where a rule that must not be missed
// belongs -- see feedback_server_side_enforcement.

type Entry = {label: string; isUnsaved: () => boolean};

const UnsavedChangesContext = createContext<{
    register: (key: string, entry: Entry) => void;
    unregister: (key: string) => void;
    pending: () => string[];
} | null>(null);

export function UnsavedChangesProvider({children}: {children: React.ReactNode}) {
    const registry = useRef(new Map<string, Entry>());

    const register = useCallback((key: string, entry: Entry) => {
        registry.current.set(key, entry);
    }, []);

    const unregister = useCallback((key: string) => {
        registry.current.delete(key);
    }, []);

    // The LABELS of everything currently holding unsaved work, so the warning can name the tab
    // rather than say "something somewhere has changed" -- which would send people looking.
    const pending = useCallback(() => {
        const names: string[] = [];
        registry.current.forEach(entry => {
            try {
                if (entry.isUnsaved()) names.push(entry.label);
            } catch {
                // A panel mid-unmount must never be able to block a lifecycle action.
            }
        });
        return names;
    }, []);

    return (
        <UnsavedChangesContext.Provider value={{register, unregister, pending}}>
            {children}
        </UnsavedChangesContext.Provider>
    );
}

/**
 * Report whether this panel is holding edits that are not on the server.
 *
 * Safe outside a provider: returns without registering, so a panel reused on a page that has no
 * lifecycle buttons costs nothing and breaks nothing.
 */
export function useReportUnsavedChanges(key: string, label: string, isUnsaved: boolean) {
    const context = useContext(UnsavedChangesContext);
    // Read through a ref so the registered closure always sees the CURRENT value without the
    // registration itself having to change on every keystroke.
    const latest = useRef(isUnsaved);
    latest.current = isUnsaved;

    useEffect(() => {
        if (!context) return;
        context.register(key, {label, isUnsaved: () => latest.current});
        return () => context.unregister(key);
    }, [context, key, label]);
}

/** What is unsaved right now. Returns an empty list outside a provider. */
export function useUnsavedChanges(): () => string[] {
    const context = useContext(UnsavedChangesContext);
    const none = useCallback(() => [], []);
    return context ? context.pending : none;
}
